import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import axios from "axios";
import { T } from "../tokens";
import { useAuthStore } from "../store";

// r3: the extension captures from its own toolbar button on the job page (activeTab),
// not through a web-page bridge — a page cannot reach another tab without broad host
// permissions. This admin page explains installation and offers the two builds.
/** The download route needs the Authorization header, which a plain link never sends. */
async function downloadBuild(token: string | null, env: "production" | "preprod"): Promise<void> {
  if (!token) return;
  const res = await axios.get(`/api/admin/extension/download?env=${env}`, {
    responseType: "blob", headers: { Authorization: `Bearer ${token}` },
  });
  const url = URL.createObjectURL(new Blob([res.data], { type: "application/zip" }));
  const a = document.createElement("a");
  a.href = url;
  a.download = `calibre-extension-${env}.zip`;
  document.body.appendChild(a);
  a.click();
  a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function Extensions() {
  const token = useAuthStore((s) => s.token);

  const adminCheck = useQuery({
    queryKey: ["admin-access-extensions", token ?? "anonymous"],
    queryFn: async () => {
      await axios.get("/api/admin/metrics");
      return true;
    },
    enabled: !!token,
    retry: false,
  });

  const heading = (
    <h1 style={{ fontFamily: T.display, fontSize: "22px", fontWeight: 700, color: T.t0, marginBottom: "8px" }}>
      Chrome extension
    </h1>
  );

  if (adminCheck.isLoading) {
    return (
      <div style={{ maxWidth: "820px" }}>
        {heading}
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginBottom: "16px" }}>Checking access...</p>
      </div>
    );
  }

  if (!adminCheck.isSuccess) {
    return (
      <div style={{ maxWidth: "820px" }}>
        {heading}
        <p style={{ fontFamily: T.body, fontSize: "13px", color: T.err, marginBottom: "16px" }}>Admin access required.</p>
      </div>
    );
  }

  const p = { fontFamily: T.body, fontSize: "12px", color: T.t2, margin: "0 0 8px", lineHeight: 1.6 } as const;
  const linkBtn = { background: "none", border: "none", padding: 0, color: T.teal, cursor: "pointer", fontFamily: T.body, fontSize: "12px", textDecoration: "underline" } as const;
  return (
    <div style={{ maxWidth: "820px" }}>
      {heading}
      <p style={{ fontFamily: T.body, fontSize: "13px", color: T.t2, marginBottom: "16px" }}>
        Open a job page in Chrome, click the Calibre toolbar button, then <strong style={{ color: T.t0 }}>Capture</strong>.
        The job is saved privately to your tracker with a match score.
      </p>
      <div style={{ background: T.s1, border: `1px solid ${T.b0}`, borderRadius: "10px", padding: "14px", marginBottom: "14px" }}>
        <p style={p}>Install: open chrome://extensions, enable Developer mode, unzip the package, click Load unpacked.</p>
        <p style={p}>
          Download:{" "}
          <button type="button" onClick={() => void downloadBuild(token, "production")} style={linkBtn}>production build</button>
          {" · "}
          <button type="button" onClick={() => void downloadBuild(token, "preprod")} style={linkBtn}>PREPROD build</button>
          . Each build talks only to its own Calibre API and shows its environment in the popup.
        </p>
        <p style={{ ...p, margin: 0 }}>
          Works on job pages that publish standard job data (most job boards and company career sites), plus LinkedIn,
          Naukri, Indeed, Greenhouse, Lever, Ashby and IIMJobs. Limit: 20 captures per day.
        </p>
      </div>
      <p style={{ fontFamily: T.body, fontSize: "12px" }}>
        <Link to="/queue" style={{ color: T.teal }}>Open your tracker</Link>
      </p>
    </div>
  );
}
